/**
 * 🔬 Decoder / Researcher explainer page — Cetalingua FM (Stage 3 polish, 1 Oct 2026)
 *
 * Same look as StationLanding (re-uses StationLanding.css) + AnalystLanding.css for the two plan cards.
 * Prices come from portal/plans.json (same file as Stripe), so they never go out of date here.
 * Screenshots: see components/common/Showcase.js (files in admin-panel/public/marketing/).
 *
 * Props: onSelectPlan({ planId, period }) — go to sign-up with that plan
 *        onAllPlans() — the full plans page (all periods)
 *        onNavigate('terms' | 'privacy' | 'contact' | 'login')
 */
import React from 'react';
import cfg from '../portal/plans.json';
import { INCLUDES } from '../portal/PortalPlans';
import Showcase from '../common/Showcase';
import '../station/StationLanding.css';
import './AnalystLanding.css';

const plan = (id) => cfg.plans.find((p) => p.id === id);
const price = (id) => `$${plan(id).prices.m1.toFixed(2)}`;

const SHOTS = [
  { file: 'analyst-spectrogram.png', kind: 'desktop', caption: 'Spectrogram of a community recording with marked detections' },
  { file: 'analyst-calltypes.png', kind: 'desktop', caption: 'Every call with its harmonics traced, call type and measurements (a West Indian manatee call)' },
  { file: 'analyst-measurements.png', kind: 'desktop', caption: 'Researcher: all measurements, click card and Wigner-Ville' },
  { file: 'analyst-app-visualizer.png', kind: 'phone', caption: 'In the app: listeners see the calls while they listen' },
];

const AnalystLanding = ({ onSelectPlan, onAllPlans, onNavigate }) => {
  const trial = plan('decoder').trialDays;
  const go = (id) => onSelectPlan({ planId: id, period: 'm1' });
  const link = (page) => (e) => { e.preventDefault(); onNavigate && onNavigate(page); };

  return (
    <div className="station-landing-container">
      {/* Hero */}
      <div className="station-hero">
        <div className="hero-content">
          <span className="hero-logo">🔬</span>
          <h1 className="hero-title">Decode What Whales and Dolphins Are Saying</h1>
          <p className="hero-subtitle">
            Explore real recordings from hydrophone stations around the world. See every call on a spectrogram,
            find out which call type it is, measure it, and build your own understanding of how marine mammals communicate.
          </p>
          <div className="hero-highlight">
            <span className="highlight-icon">✨</span>
            <span className="highlight-text">
              {trial ? `Try Decoder free for ${trial} days – cancel any time before it ends and pay nothing.`
                     : 'Cancel any time.'}
            </span>
          </div>
          <div className="analyst-hero-buttons">
            <button className="hero-cta" onClick={() => go('decoder')}>
              {trial ? `Start ${trial}-day free trial →` : 'Become a Decoder →'}
            </button>
            <button className="analyst-ghost" onClick={onAllPlans}>Compare all plans</button>
          </div>
        </div>
      </div>

      {/* Who it is for */}
      <div className="simple-section">
        <h2>Two Ways In</h2>
        <p className="section-intro">Curious listener or working scientist – pick the depth you need.</p>
        <div className="simple-steps">
          <div className="simple-step">
            <div className="step-icon">🎧</div>
            <h3>Decoder</h3>
            <p>For enthusiasts, students, naturalists and guides who want to go beyond listening.</p>
          </div>
          <div className="simple-arrow analyst-or">or</div>
          <div className="simple-step">
            <div className="step-icon">🧪</div>
            <h3>Researcher</h3>
            <p>For scientists and research groups who need measurements, exports and the full analysis.</p>
          </div>
        </div>
      </div>

      {/* Screenshots */}
      <Showcase intro="A look at the tools – the same recordings listeners hear in the Cetalingua FM app."
                items={SHOTS} />

      {/* What you can do */}
      <div className="benefits-section">
        <h2>What You Can Do</h2>
        <div className="benefits-grid">
          <div className="benefit-card featured">
            <div className="benefit-icon">🌊</div>
            <h3>Analyse Community Recordings</h3>
            <p>Open recordings shared by stations worldwide – many sites, seasons and species in one place.</p>
          </div>
          <div className="benefit-card featured">
            <div className="benefit-icon">🏷️</div>
            <h3>Mark Detections and Call Types</h3>
            <p>Mark the calls you find and match them to call types with clear cheat sheets for each species.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">📈</div>
            <h3>Measure Every Call</h3>
            <p>Researcher: frequency, duration, harmonics, contour and more – plus a click card and Wigner-Ville view.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">📤</div>
            <h3>Export Your Work</h3>
            <p>Decoder: summary export. Researcher: full export of tables, figures and your edits.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">📚</div>
            <h3>Call Library</h3>
            <p>Researcher: browse the library of labelled calls to compare against your own findings.</p>
          </div>
          <div className="benefit-card">
            <div className="benefit-icon">🐬</div>
            <h3>Growing Species List</h3>
            <p>Bottlenose dolphins, humpback whales and manatees today – more species are being added.</p>
          </div>
        </div>
      </div>

      {/* Pricing */}
      <div className="pricing-section">
        <h2>Simple Pricing</h2>
        <div className="analyst-plans">
          {['decoder', 'researcher'].map((id) => (
            <div key={id} className={`station-pricing-card analyst-plan${id === 'researcher' ? ' analyst-plan-pro' : ''}`}>
              <div className="pricing-header">
                <span className="pricing-icon">{id === 'decoder' ? '🎧' : '🧪'}</span>
                <h3>{plan(id).name}</h3>
              </div>
              <div className="pricing-price">
                <span className="price-amount">{price(id)}</span>
                <span className="price-period">/month</span>
              </div>
              <div className="pricing-features">
                <ul>{INCLUDES[id].map((t) => <li key={t}>✓ {t}</li>)}</ul>
              </div>
              <button className="pricing-button" onClick={() => go(id)}>
                {plan(id).trialDays ? `Start ${plan(id).trialDays}-day free trial →` : `Choose ${plan(id).name} →`}
              </button>
            </div>
          ))}
        </div>
        <p className="analyst-note">
          Save with 3, 6 or 12 months (12 months = 2 months free) – <a href="#" onClick={(e) => { e.preventDefault(); onAllPlans(); }}>see all plans</a>.
          Universities and research groups: ask us for an academic discount code (20 % off) via <a href="#" onClick={link('contact')}>Contact Us</a>.
        </p>
      </div>

      {/* FAQ */}
      <div className="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>Do I need to own a hydrophone?</h4>
            <p>No. Decoder and Researcher work on recordings that stations share with the community. If you have your own recordings, a Station plan lets you upload them.</p>
          </div>
          <div className="faq-item">
            <h4>How does the free trial work?</h4>
            <p>Decoder and Researcher are free for {trial || 7} days. You enter a card, but nothing is charged if you cancel before the trial ends (Subscription → Manage subscription).</p>
          </div>
          <div className="faq-item">
            <h4>Can I switch from Decoder to Researcher?</h4>
            <p>Yes, any time in Subscription → Manage subscription. Researcher includes all Decoder tools.</p>
          </div>
          <div className="faq-item">
            <h4>Can I combine it with a station?</h4>
            <p>Yes – there are combined Station + Decoder and Station + Researcher plans on the plans page.</p>
          </div>
          <div className="faq-item">
            <h4>Is there a discount for students and universities?</h4>
            <p>Yes, 20 % with an academic code. Contact us with your institution's name.</p>
          </div>
          <div className="faq-item">
            <h4>Do I need special software?</h4>
            <p>No. Everything runs in your web browser in the Cetalingua portal.</p>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="cta-section">
        <h2>Ready to Start Decoding?</h2>
        <p>Join listeners and scientists exploring the sounds of the ocean</p>
        <div className="cta-buttons">
          <button className="cta-button primary" onClick={() => go('decoder')}>
            🎧 Decoder – {price('decoder')}/mo{trial ? `, ${trial} days free` : ''}
          </button>
          <button className="cta-button secondary" onClick={() => go('researcher')}>
            🧪 Researcher – {price('researcher')}/mo{plan('researcher').trialDays ? `, ${plan('researcher').trialDays} days free` : ''}
          </button>
        </div>
      </div>

      {/* Footer */}
      <div className="station-footer">
        <div className="footer-links">
          <a href="#" onClick={link('terms')}>Terms of Service</a>
          <span className="footer-divider">•</span>
          <a href="#" onClick={link('privacy')}>Privacy Policy</a>
          <span className="footer-divider">•</span>
          <a href="#" onClick={link('contact')}>Contact Us</a>
        </div>
        <p className="footer-copyright">© {new Date().getFullYear()} Cetalingua FM. All rights reserved.</p>
        <p className="existing-partner">
          Already have an account? <a href="#" onClick={link('login')}>Sign in here</a>
        </p>
      </div>
    </div>
  );
};

export default AnalystLanding;
